import { defineStore } from "pinia";
import { PublicClientApplication, InteractionType } from "@azure/msal-browser";
import type { AuthenticationResult } from "@azure/msal-browser";
import { AuthCodeMSALBrowserAuthenticationProvider } from "@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser";
import { Client } from "@microsoft/microsoft-graph-client";
import { login_backend, get_current_user } from "@/core/services/ApiService";
import {
  writeAuthData,
  writeUserData,
  readAuthData,
  clearAuthStorage,
} from "@/services/authStorage";
import { runtimeConfig } from "@/config/runtime";
import { log } from "@/logging";

export const pca = new PublicClientApplication({
  auth: {
    clientId: runtimeConfig.clientId,
    authority: runtimeConfig.authority,
    redirectUri: runtimeConfig.redirectUri,
  },
  cache: {
    cacheLocation: "sessionStorage",
    // false: no cookie fallback — this app targets modern browsers only.
    // true would write auth state to cookies, undermining sessionStorage isolation.
    storeAuthStateInCookie: false,
  },
});

export function convertBlobToBase64(
  blob: Blob,
): Promise<string | ArrayBuffer | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
}

export async function convertImageToBase64(imagePath: string) {
  const response = await fetch(imagePath);
  const blob = await response.blob();
  return convertBlobToBase64(blob);
}

export const GRAPH_SCOPES = ["User.Read", "Calendars.read", "Mail.Send"];

/**
 * Shared post-token flow: fetch Graph /me + photo, call backend login,
 * write auth/user to sessionStorage. Returns the gClient on success, null on failure.
 * Fails closed on missing expiresOn or account — does not fall back to arbitrary expiry.
 */
async function _buildSession(
  tokenResult: AuthenticationResult,
  gClient: Client,
): Promise<Client | null> {
  if (!tokenResult.expiresOn || !tokenResult.account) return null;

  const email = tokenResult.account.username;
  const idToken = tokenResult.idToken;
  const expiryTime = tokenResult.expiresOn.getTime();

  const user = await gClient.api("/me").get();

  let profilePicture = null;
  try {
    profilePicture = await gClient.api("me/photo/$value").get();
  } catch {
    // No profile picture set — use default
  }

  const base64data = profilePicture
    ? await convertBlobToBase64(profilePicture)
    : await convertImageToBase64("media/avatars/blank.png");

  const response = await login_backend(email, idToken);
  if (!response.ok) return null;

  const loginResult = await response.json();
  writeUserData(loginResult);
  writeAuthData({
    id: loginResult.id,
    isAuthenticated: true,
    tokenExpiry: expiryTime,
    email,
    name: user.displayName,
    // convertBlobToBase64 returns string | ArrayBuffer | null; in practice it
    // is always a data URL string here. ArrayBuffer cannot occur from readAsDataURL.
    profilePicture: (typeof base64data === "string" ? base64data : null) ?? "",
    idToken,
    description: loginResult.description,
    role: loginResult.role,
    loginTime: Date.now(),
  });
  return gClient;
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    graphClient: null as Client | null,
  }),

  actions: {
    async initializeAuth(): Promise<Client | null> {
      await pca.initialize();
      await pca.handleRedirectPromise();

      const [account] = pca.getAllAccounts();
      if (!account) return null;
      const result = await pca.acquireTokenSilent({
        account,
        scopes: GRAPH_SCOPES,
      });

      // Fail closed — do not grant a fallback session if token metadata is incomplete
      if (!result?.expiresOn) {
        this.logout();
        return null;
      }

      const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(pca, {
        account,
        interactionType: InteractionType.Popup,
        scopes: GRAPH_SCOPES,
      });
      const gClient = Client.initWithMiddleware({ authProvider });
      const sessionClient = await _buildSession(result, gClient);
      if (sessionClient) this.graphClient = sessionClient;
      return sessionClient;
    },

    async login(): Promise<Client | null> {
      await pca.initialize();
      try {
        const result = await pca.acquireTokenPopup({ scopes: GRAPH_SCOPES });

        if (!result.account) throw new Error("Could not authenticate");

        const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(
          pca,
          {
            account: result.account,
            interactionType: InteractionType.Popup,
            scopes: GRAPH_SCOPES,
          },
        );
        const gClient = Client.initWithMiddleware({ authProvider });
        const sessionClient = await _buildSession(result, gClient);
        if (sessionClient) this.graphClient = sessionClient;
        return sessionClient;
      } catch (error) {
        // Log sanitized message only — MSAL error objects may contain scope/email metadata
        log.error(
          "Authentication error",
          error instanceof Error ? error.message : "Unknown error",
        );
        throw error;
      }
    },

    logout(): void {
      clearAuthStorage();
      this.graphClient = null;
      // Best-effort MSAL token cache flush — prevents silent token reuse after logout
      const pcaUnknown = pca as unknown as { clearCache?: () => Promise<void> };
      if (typeof pcaUnknown.clearCache === "function") {
        pcaUnknown.clearCache().catch(() => undefined);
      }
    },

    // Design decision: no in-memory cache here. Reading from sessionStorage on
    // every call is intentional — it costs ~1 JSON.parse but guarantees we
    // never serve a stale auth state.
    checkAuth(): boolean {
      const authData = readAuthData();
      const isAuthenticated = authData && authData.isAuthenticated;
      const tokenExpiry = authData ? authData.tokenExpiry : null;
      const isTokenExpired = tokenExpiry ? Date.now() > tokenExpiry : true;
      if (isAuthenticated) {
        if (isTokenExpired) {
          return false;
        }
        return true;
      }
      return false;
    },

    async updateUserProfile(): Promise<void> {
      const authData = readAuthData();
      if (!authData?.isAuthenticated) return;

      const response = await get_current_user();
      if (!response.ok) {
        // Refresh failing means role/user_modules go stale for the rest of the
        // session, so it must be visible rather than swallowed. Not fatal —
        // the sessionStorage blob from login remains in force.
        log.warn(`Could not refresh user profile (HTTP ${response.status})`);
        return;
      }

      const result = await response.json();
      writeUserData(result);

      // Re-read auth to avoid overwriting concurrent token refreshes
      const currentAuth = readAuthData();
      if (!currentAuth) return;
      writeAuthData({
        ...currentAuth,
        id: result.id,
        description: result.description,
        role: result.role,
      });
    },
  },
});
